import type { ChangeAllLegChange, OrderLeg } from '../../../services/orders.service';

/**
 * The two LEG fields /pro may fan out — pickup origin and terminate destination.
 *
 * THE FIRST LEG FIELDS TO BECOME FANNABLE, and deliberately not wired through the
 * order-field machinery beside them. `BulkFieldCheckbox` asks
 * `field_definitions.is_change_all`, a registry scoped to `orders`/`orderscurrent`
 * that has no row for a leg column and would answer "not fannable" for both of
 * these. More to the point, an order field is one column on one row, while a leg
 * field has to find its row first — and which row that is differs per pro.
 *
 * WHICH ROW: the leg the ORDER'S OWN POINTER names (`originateleg`/`terminateleg`),
 * never a legnum. Pro A's pickup may be leg 1 and pro B's leg 2 — 1,541 of 32,211
 * live orders have originateleg != 1 — so the fan-out sends a ROLE and each pro
 * resolves its own leg server-side (`build_external_context`). Sending the anchor's
 * legnum would write the wrong stop on every pro that disagrees with it.
 *
 * The value sent is the ANCHOR's current code for that leg, matching how a flagged
 * order field behaves: ticking a field the user did not edit copies what the anchor
 * already has.
 */
export const LEG_LOCATION_BULK_FIELDS = {
  pickupOrigin: {
    role: 'originate' as const,
    column: 'origcode' as const,
    /** OrderLeg key holding the value on the anchor. */
    legKey: 'origcode' as const,
    label: 'Pickup Location',
  },
  terminateDest: {
    role: 'terminate' as const,
    column: 'destcode' as const,
    legKey: 'destcode' as const,
    label: 'Terminate Location',
  },
} as const;

export type LegLocationBulkKey = keyof typeof LEG_LOCATION_BULK_FIELDS;

export const LEG_LOCATION_BULK_KEYS = Object.keys(
  LEG_LOCATION_BULK_FIELDS,
) as LegLocationBulkKey[];

/** The order's own leg pointers, as the /pro order detail serves them. */
export interface LegPointers {
  originateLeg?: number | null;
  terminateLeg?: number | null;
}

const pointerFor = (key: LegLocationBulkKey, pointers: LegPointers): number =>
  Number(
    (LEG_LOCATION_BULK_FIELDS[key].role === 'originate'
      ? pointers.originateLeg
      : pointers.terminateLeg) ?? 0,
  );

/**
 * Is THIS leg the one the given bulk field targets on the anchor?
 *
 * Drives whether the checkbox is drawn beside a cell at all, so it reads the
 * order's pointer rather than the leg's own legtype/legAction2. A leg can look
 * like a pickup without being the leg `originateleg` names, and the fan-out
 * resolves by pointer — the checkbox has to appear on the leg that will actually
 * be written, or it promises something else.
 */
export function isBulkTargetLeg(
  key: LegLocationBulkKey, leg: Pick<OrderLeg, 'legnum'>, pointers: LegPointers,
): boolean {
  const legnum = pointerFor(key, pointers);
  return legnum > 0 && Number(leg.legnum) === legnum;
}

/**
 * The leg rows for a fan-out: one per ticked field, carrying the anchor's current
 * location code and a ROLE for the server to resolve per pro.
 *
 * A ticked field whose leg is missing on the ANCHOR emits nothing — there is no
 * value to copy. (Targets missing the leg are a different question, answered per
 * pro by the server and surfaced in the review modal.)
 */
export function buildLegLocationChangeAllRows(
  checked: ReadonlySet<string>,
  effectiveLegs: readonly OrderLeg[],
  pointers: LegPointers,
): ChangeAllLegChange[] {
  const rows: ChangeAllLegChange[] = [];
  for (const key of LEG_LOCATION_BULK_KEYS) {
    if (!checked.has(key)) continue;
    const spec = LEG_LOCATION_BULK_FIELDS[key];
    const legnum = pointerFor(key, pointers);
    if (!legnum) continue;
    const leg = effectiveLegs.find((l) => Number(l.legnum) === legnum);
    if (!leg) continue;
    const value = Number(leg[spec.legKey] ?? 0);
    if (!value) continue;
    rows.push({ match: { role: spec.role }, column: spec.column, newValue: value });
  }
  return rows;
}
